import { gql, type ApolloClient } from "@apollo/client";
import { parseCurrentTerms, type ParsedTerms, type TermsLocale } from "./terms";

/**
 * THE TERMS IN FORCE, from the public booking API (booking-v1@1.14.0).
 *
 * Copied verbatim from the pinned artifact's
 * `contract/booking-v1/operations/CurrentTerms.graphql` (Backend 180127b3) and
 * validated against the pinned schema by `quality:contracts`.
 */
export const CURRENT_TERMS = ({"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"CurrentTerms"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"locale"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"TermsLocale"}}},"defaultValue":{"kind":"EnumValue","value":"EN"},"directives":[]}],"directives":[],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"currentTerms"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"locale"},"value":{"kind":"Variable","name":{"kind":"Name","value":"locale"}}}],"directives":[],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"policyId"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"version"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"contentHash"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"locale"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"documentUrl"},"arguments":[],"directives":[]}]}}]}}]})









;

/**
 * One read of the Terms, or `unavailable`. Never throws.
 *
 * Its OWN document, like the 1.12 terms read on /a/: a backend that refuses
 * it refuses only this read, never the rest of Review.
 *
 * `no-cache`: every read is a fresh answer. The whole point of re-reading
 * after TERMS_STALE is to see what is in force NOW, and a cached answer is
 * the one that was just refused.
 *
 * Every failure is `unavailable` (frame 4.5, Confirm off) with ONE exception,
 * `termsFieldAbsent` below: a backend whose schema has no `currentTerms` at
 * all. Network loss, TERMS_UNAVAILABLE, any 5xx, an App Check or auth
 * refusal, any other validation error and a malformed answer all leave
 * nothing that could be echoed, so Confirm stays off and the person can load
 * the terms again.
 */
export type TermsRead = ParsedTerms | { readonly kind: "unavailable" };

/**
 * What graphql-js says when a schema predates the Terms: the field is not on
 * the type, or a type the document names does not exist (`:
 * TermsLocale!`; `CurrentTerms`). Anchored, so `Cannot query field
 * "documentUrl" on type "CurrentTerms"` (the type EXISTS, a field is
 * missing) is not mistaken for absence.
 */
const ABSENCE_MESSAGES: readonly RegExp[] = [
  /^Cannot query field "currentTerms" on type "[A-Za-z_][A-Za-z0-9_]*"\./,
  /^Unknown type "(?:TermsLocale|CurrentTerms)"\./,
];

/**
 * IS THIS A BACKEND WITHOUT THE `currentTerms` FIELD? (Web Director, rc.8.)
 *
 * The field and the requirement arrive together in booking-v1 1.13 (and
 * client-v1 1.2): a schema that cannot answer `currentTerms` cannot require
 * `termsAcceptance` either. So that ONE refusal means "no Terms on this
 * backend", exactly as a null answer does: no checkbox, nothing sent.
 *
 * Deliberately narrow, because widening it turns an outage into silently
 * booking without the Terms once they are required:
 *  - every error entry must be GRAPHQL_VALIDATION_FAILED, and EACH must say,
 *    in GraphQL's own words, that `currentTerms`, `TermsLocale` or
 *    `CurrentTerms` does not exist (reviewer P2: an old core answers the
 *    ClientSignupTerms document with both kinds);
 *  - the HTTP status, when there is one, must be the 400 a validation
 *    refusal is sent on. A 5xx carrying the same body is a gateway's word,
 *    not the schema's (the transport rule in booking/operations.ts).
 * Anything else, including a validation error about something else, is not
 * this.
 */
export function termsFieldAbsent(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const candidate = error as {
    errors?: unknown;
    bodyText?: unknown;
    statusCode?: unknown;
    response?: { status?: unknown } | null;
  };
  const status =
    typeof candidate.statusCode === "number"
      ? candidate.statusCode
      : typeof candidate.response?.status === "number"
        ? candidate.response.status
        : null;
  if (status !== null && status !== 400) return false;

  const entries: unknown[] = [];
  if (Array.isArray(candidate.errors)) entries.push(...candidate.errors);
  if (typeof candidate.bodyText === "string") {
    try {
      const parsed = JSON.parse(candidate.bodyText) as { errors?: unknown };
      if (Array.isArray(parsed.errors)) entries.push(...parsed.errors);
    } catch {
      return false;
    }
  }
  if (entries.length === 0) return false;
  for (const entry of entries) {
    if (!entry || typeof entry !== "object") return false;
    const { message, extensions } = entry as { message?: unknown; extensions?: { code?: unknown } };
    if (extensions?.code !== "GRAPHQL_VALIDATION_FAILED") return false;
    if (typeof message !== "string" || !ABSENCE_MESSAGES.some((pattern) => pattern.test(message))) return false;
  }
  return true;
}

/** An Apollo answer or rejection, as a TermsRead. Shared by both Terms reads. */
export function termsReadFrom(outcome: { data?: unknown; error?: unknown } | { thrown: unknown }, pick: (data: unknown) => unknown): TermsRead {
  if ("thrown" in outcome) {
    return termsFieldAbsent(outcome.thrown) ? { kind: "none" } : { kind: "unavailable" };
  }
  if (outcome.error) return termsFieldAbsent(outcome.error) ? { kind: "none" } : { kind: "unavailable" };
  if (!outcome.data) return { kind: "unavailable" };
  const parsed = parseCurrentTerms(pick(outcome.data));
  return parsed.kind === "malformed" ? { kind: "unavailable" } : parsed;
}

export async function readBookingTerms(client: ApolloClient, locale: TermsLocale): Promise<TermsRead> {
  try {
    const result = await client.query<{ currentTerms?: unknown }>({
      query: CURRENT_TERMS,
      variables: { locale },
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    });
    return termsReadFrom(result, (data) => (data as { currentTerms?: unknown }).currentTerms);
  } catch (thrown) {
    return termsReadFrom({ thrown }, () => undefined);
  }
}
